import {Tooltip} from 'platform/components';
import {Box, HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import {useId} from 'react';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {type Nullish, type RequiredTestIdProps, suffixTestId} from 'shared/web';

interface DailyStockTakeSummaryTileProps extends RequiredTestIdProps {
  label: string;
  value: string;
  tooltip?: string | Nullish;
  /** Small line under the value, e.g. the number of cards behind it. */
  note?: string | Nullish;
  /** The tile that carries the day's headline figure is tinted to stand out from the two inputs. */
  isHighlighted?: boolean;
}

export function DailyStockTakeSummaryTile(props: DailyStockTakeSummaryTileProps) {
  const tooltipDescriptionId = useId();

  return (
    <Box
      height="100%"
      paddingHorizontal={3}
      paddingVertical={2}
      borderRadius="small"
      border="1px solid"
      backgroundColor={props.isHighlighted ? 'palettes.blue.10.100' : 'palettes.neutral.10.100'}
      borderColor={props.isHighlighted ? 'palettes.blue.30.100' : 'palettes.neutral.40.100'}
    >
      <VStack spacing={0}>
        <HStack spacing={1} align="center">
          <Text size="xSmall" color="secondary" data-testid={suffixTestId('label', props)}>
            {props.label}
          </Text>
          <Show when={isNotNilOrEmpty(props.tooltip) ? props.tooltip : null}>
            {(tooltip) => (
              <>
                <Tooltip description={<span role="tooltip">{tooltip}</span>}>
                  <div
                    role="img"
                    aria-label={props.label}
                    aria-describedby={tooltipDescriptionId}
                    tabIndex={0}
                  >
                    <Icon value="action/info" color="text.secondary" size={4} />
                  </div>
                </Tooltip>
                <span id={tooltipDescriptionId} hidden>
                  {tooltip}
                </span>
              </>
            )}
          </Show>
        </HStack>
        <Text
          size="base"
          alternative
          color={props.isHighlighted ? 'link' : 'primary'}
          data-testid={suffixTestId('value', props)}
        >
          {props.value}
        </Text>
        <Show when={isNotNil(props.note)}>
          <Text size="xxSmall" color="secondary" data-testid={suffixTestId('note', props)}>
            {props.note}
          </Text>
        </Show>
      </VStack>
    </Box>
  );
}
